import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { setAllEntities, withEntities } from '@ngrx/signals/entities';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { tapResponse } from '@ngrx/operators';
import { StorageLocation, StorageLocationArticle } from '@knust/api-interfaces';
import { HttpClient, HttpParams } from '@angular/common/http';
import { inject } from '@angular/core';
import {
  debounceTime,
  distinctUntilChanged,
  EMPTY,
  pipe,
  switchMap,
  tap,
} from 'rxjs';
import {
  STORAGE_LOCATION_ARTICLES_URL,
  STORAGE_LOCATIONS_URL,
} from '../constants/api.constants';
import { PaginatedResponse } from '../interfaces/paginated-response';
import {
  DEFAULT_PAGE_SIZE,
  SEARCH_DEBOUNCE_TIME,
  toPaginationParams,
} from './pagination';
import { propagateHttpErrors } from '../interceptors/http-error.interceptor';

// Von der API geladene Datensätze haben immer eine ID
type StorageLocationArticleEntity = StorageLocationArticle & { id: number };

type StorageLocationArticlesState = {
  storageLocationId: number | null;
  storageLocation: StorageLocation | null;
  storageLocationLoading: boolean;
  loading: boolean;
  search: string;
  pageIndex: number;
  pageSize: number;
  total: number;
};

const initialState: StorageLocationArticlesState = {
  storageLocationId: null,
  storageLocation: null,
  storageLocationLoading: false,
  loading: false,
  search: '',
  pageIndex: 0,
  pageSize: DEFAULT_PAGE_SIZE,
  total: 0,
};

/**
 * Artikel mit Bestand in einem Lagerort. Der Store wird von der StorageLocationArticlesComponent
 * bereitgestellt, damit jeder Lagerort mit leerer Suche und auf der ersten Seite startet.
 */
export const StorageLocationArticlesStore = signalStore(
  withState<StorageLocationArticlesState>(initialState),
  withEntities<StorageLocationArticleEntity>(),
  withMethods((store, http = inject(HttpClient)) => {
    const loadArticles = rxMethod<void>(
      pipe(
        switchMap(() => {
          const storageLocationId = store.storageLocationId();

          if (storageLocationId === null) {
            return EMPTY;
          }

          patchState(store, { loading: true });

          // storageLocationId=0 bedeutet bei dieser Route "kein Scan-Code" und blendet Artikel ohne Bestand aus
          const params = toPaginationParams(
            store,
            new HttpParams()
              .set('storageLocationId', 0)
              .set('storageLocation', storageLocationId)
              .set('sortBy', 'article.title:ASC'),
          );

          return http
            .get<PaginatedResponse<StorageLocationArticleEntity>>(
              STORAGE_LOCATION_ARTICLES_URL,
              { params, context: propagateHttpErrors() },
            )
            .pipe(
              tapResponse({
                next: (response) =>
                  patchState(store, setAllEntities(response.items), {
                    total: response.meta.totalItems,
                    loading: false,
                  }),
                // Fehler meldet der HttpErrorInterceptor; propagateHttpErrors, damit loading zurückgesetzt wird
                error: () => patchState(store, { loading: false }),
              }),
            );
        }),
      ),
    );

    const loadStorageLocation = rxMethod<number>(
      pipe(
        tap(() =>
          patchState(store, {
            storageLocation: null,
            storageLocationLoading: true,
          }),
        ),
        switchMap((storageLocationId) =>
          http
            .get<StorageLocation>(STORAGE_LOCATIONS_URL + '/' + storageLocationId, {
              context: propagateHttpErrors(),
            })
            .pipe(
              tapResponse({
                next: (storageLocation) =>
                  patchState(store, {
                    storageLocation,
                    storageLocationLoading: false,
                  }),
                error: () =>
                  patchState(store, { storageLocationLoading: false }),
              }),
            ),
        ),
      ),
    );

    // Nicht "search" nennen: Methoden überschreiben im SignalStore gleichnamige State-Signale
    const setSearch = rxMethod<string>(
      pipe(
        debounceTime(SEARCH_DEBOUNCE_TIME),
        distinctUntilChanged(),
        tap((search) => {
          patchState(store, { search, pageIndex: 0 });
          loadArticles();
        }),
      ),
    );

    return {
      setStorageLocation(storageLocationId: number) {
        patchState(store, setAllEntities<StorageLocationArticleEntity>([]), {
          storageLocationId,
          pageIndex: 0,
          total: 0,
        });
        loadStorageLocation(storageLocationId);
        loadArticles();
      },
      reload() {
        loadArticles();
      },
      setSearch,
      setPage(pageIndex: number, pageSize: number) {
        patchState(store, { pageIndex, pageSize });
        loadArticles();
      },
    };
  }),
);
